import { Dispatch } from 'react';
import { UseMutationOptions, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useSnackbarStack } from '../../providers/SnackbarProvider.tsx';
import useErrorHandler from './useErrorHandler.ts';
import useRequestMutation, { IRequestMutationOptions } from './useRequestMutation.ts';

export interface IFastRequestOptions
  extends UseMutationOptions<any, any, any, any>,
    Pick<IRequestMutationOptions, 'invalidateArgs'> {
  ignoreErrors?: string[];
}

const useFastRequest = (
  requestFn: Dispatch<any | void>,
  { onError, invalidateArgs, ignoreErrors, ...mutationOptions }: IFastRequestOptions,
  showSnackbar = true,
): [(arg?: any) => void, UseMutationResult<any>] => {
  const { t } = useTranslation('shared/validation');
  const { pushSnackbar } = useSnackbarStack();
  const handleError = useErrorHandler(undefined, ignoreErrors, !showSnackbar);

  const mutation = useRequestMutation({
    invalidateArgs,
    onError: (...args) => {
      onError?.(...args);
      handleError(args[0]);
    },
    mutationFn: requestFn as any,
    ...mutationOptions,
  });

  const makeRequest = async (payload: any = {}) => {
    try {
      mutation.mutate(payload);
    } catch (e: any) {
      if (showSnackbar) {
        pushSnackbar({ text: e.message || t('oops') });
      }
    }
  };

  return [makeRequest, mutation as UseMutationResult<any>];
};

export default useFastRequest;
